/**
 * Obscure, unlinked base path for the admin panel. It is intentionally not the word
 * "admin" so it is not guessable, is excluded from robots.txt (search + AI crawlers),
 * and is never linked from any public page or nav.
 */
export const ADMIN_BASE_PATH = "/balaguraiah";
export const ADMIN_LOGIN_PATH = `${ADMIN_BASE_PATH}/login`;

export type AdminTab =
  | "dashboard"
  | "platform"
  | "finance"
  | "economy"
  | "rewards"
  | "data"
  | "users"
  | "posts"
  | "drifts"
  | "parallels"
  | "reports"
  | "promotions"
  | "ads"
  | "activity";

export type NavItem = { id: AdminTab; label: string; badgeKey?: "reports" };

export type NavGroup = { title: string; items: NavItem[] };

export const adminNavGroups: NavGroup[] = [
  {
    title: "General",
    items: [
      { id: "dashboard", label: "Dashboard" },
      { id: "platform", label: "Platform" },
      { id: "rewards", label: "Sparks & tiers" },
      { id: "reports", label: "Reports", badgeKey: "reports" },
    ],
  },
  {
    title: "Finance",
    items: [
      { id: "finance", label: "Revenue & wallet" },
      { id: "economy", label: "Marketplace & shop" },
    ],
  },
  {
    title: "Management",
    items: [
      { id: "data", label: "Data" },
      { id: "users", label: "Users" },
      { id: "posts", label: "Frames" },
      { id: "drifts", label: "Drifts" },
      { id: "parallels", label: "Parallels" },
      { id: "promotions", label: "Promotions" },
      { id: "ads", label: "AdMob" },
      { id: "activity", label: "Activity log" },
    ],
  },
];

/** Requires external APIs or future work — not aggregatable from Postgres alone. */
export const ADMIN_NOT_IMPLEMENTED = [
  {
    area: "AdMob revenue (₹)",
    detail: "Google AdMob earnings API is not connected. Config toggles and ad-free user counts are shown instead.",
  },
  {
    area: "FCM delivery & open rates",
    detail: "Push outbox volume is tracked, but delivery confirmations and notification taps are not stored.",
  },
  {
    area: "GST tax invoices",
    detail: "Automated invoice PDFs for Razorpay or promotions are not generated.",
  },
  {
    area: "Multi-admin roles",
    detail: "Admins are listed read-only. Role-based permissions (moderator vs finance) are not built.",
  },
] as const;

export function formatInr(paise: number | undefined | null): string {
  const p = paise ?? 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(p / 100);
}

export function formatCoins(coins: number | undefined | null): string {
  return new Intl.NumberFormat("en-IN").format(coins ?? 0);
}

export function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]): void {
  const escape = (v: string | number | null | undefined) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
